// Clojure course content — Part 4 (lessons): errors, lazy seqs, protocols, files

export const lessonsPart4 = [
  {
    id: "errors",
    title: "Handling Errors",
    group: "Advanced Clojure",
    summary: "Deal gracefully with things that go wrong.",
    content: [
      { t: "p", text: "Sometimes code fails: bad input, a missing value, an impossible calculation. Clojure lets you catch these failures so your program can respond instead of crashing. The tool is try / catch." },
      { t: "code", code: "(try\n  (throw (js/Error. \"something broke\"))\n  (catch :default e\n    (str \"Recovered from: \" (.-message e))))" },
      { t: "p", text: "Inside try you put the risky code. If it throws an error, control jumps to the catch block, where e is the error you can inspect. Here (.-message e) reads the error's message. Using :default catches any kind of error." },
      { t: "h", text: "throw and ex-info" },
      { t: "p", text: "You can raise your own errors. ex-info creates a rich error carrying a message AND a map of extra data you can read later with ex-data." },
      { t: "code", code: "(try\n  (throw (ex-info \"Invalid age\" {:value -5}))\n  (catch :default e\n    (ex-data e)))" },
      { t: "h", text: "finally" },
      { t: "p", text: "A finally block always runs at the end — whether or not there was an error. It's used for cleanup." },
      { t: "code", code: "(def steps (atom []))\n(try\n  (swap! steps conj :work)\n  (catch :default e (swap! steps conj :caught))\n  (finally (swap! steps conj :cleanup)))\n@steps" },
      { t: "note", text: "Prefer designing functions so errors are rare (return nil or a sensible default). Reach for try/catch when talking to the outside world or handling genuinely exceptional situations." },
    ],
    exercises: [
      {
        prompt: "Write `attempt` that takes a zero-argument function f, calls it, and returns [:ok result] if it succeeds or [:error] if it throws.",
        starter: "(defn attempt [f]\n  )",
        tests: ["(= (attempt (fn [] 5)) [:ok 5])", "(= (attempt (fn [] (throw (js/Error. \"x\")))) [:error])"],
        solution: "(defn attempt [f]\n  (try\n    [:ok (f)]\n    (catch :default e\n      [:error])))",
        hint: "Wrap (f) in a try; return [:ok (f)] and in the catch return [:error].",
      },
      {
        prompt: "Write `error-code` that calls function f; if it throws an ex-info, return its :code from ex-data, otherwise return :ok.",
        starter: "(defn error-code [f]\n  )",
        tests: ["(= (error-code (fn [] 1)) :ok)", "(= (error-code (fn [] (throw (ex-info \"bad\" {:code 404})))) 404)"],
        solution: "(defn error-code [f]\n  (try\n    (f)\n    :ok\n    (catch :default e\n      (:code (ex-data e)))))",
        hint: "In the try, call (f) then return :ok. In the catch, return (:code (ex-data e)).",
      },
    ],
  },

  {
    id: "lazy",
    title: "Lazy Sequences",
    group: "Advanced Clojure",
    summary: "Work with sequences that are computed only as needed — even infinite ones.",
    content: [
      { t: "p", text: "Many of Clojure's sequences are lazy: their elements aren't calculated until something actually asks for them. This is powerful — it means you can describe an INFINITE sequence and only ever compute the part you use." },
      { t: "p", text: "range with no arguments is an infinite sequence of 0, 1, 2, 3, … Because it's lazy, you can safely take just the first few." },
      { t: "code", code: "(take 5 (range))" },
      { t: "p", text: "Laziness flows through map and filter too. Here we square an infinite sequence but only realise the first 5 results." },
      { t: "code", code: "(take 5 (map #(* % %) (range)))" },
      { t: "h", text: "iterate" },
      { t: "p", text: "iterate builds an infinite sequence by repeatedly applying a function: x, (f x), (f (f x)), … Great for things like doubling." },
      { t: "code", code: "(take 6 (iterate #(* 2 %) 1))" },
      { t: "h", text: "repeatedly" },
      { t: "p", text: "repeatedly builds a lazy sequence by calling a function over and over — handy when each element comes from doing something rather than transforming a previous value. Give it a function of no arguments." },
      { t: "code", code: "(take 4 (repeatedly (fn [] :ping)))" },
      { t: "p", text: "You can also ask for a fixed number directly. This is often used with something that changes each call, like a random number:" },
      { t: "code", code: "(repeatedly 5 (fn [] (rand-int 100)))" },
      { t: "p", text: "Notice the difference from iterate: iterate feeds each result into the next call, while repeatedly ignores the previous value and just calls the function again — perfect for independent, generated values." },
      { t: "h", text: "Building your own" },
      { t: "p", text: "lazy-seq lets you define your own lazy sequences. This one counts upward forever, but only as far as you look." },
      { t: "code", code: "(defn from [n]\n  (lazy-seq (cons n (from (inc n)))))\n\n(take 4 (from 100))" },
      { t: "note", text: "Rule of thumb: never try to fully realise an infinite sequence (no count, no printing the whole thing). Always bound it with take, or a filter that eventually stops." },
    ],
    exercises: [
      {
        prompt: "Write `first-squares` that returns a lazy sequence's first n square numbers, starting from 0. e.g. (first-squares 4) => (0 1 4 9).",
        starter: "(defn first-squares [n]\n  )",
        tests: ["(= (first-squares 4) '(0 1 4 9))", "(= (first-squares 1) '(0))", "(= (first-squares 5) '(0 1 4 9 16))"],
        solution: "(defn first-squares [n]\n  (take n (map #(* % %) (range))))",
        hint: "Use (take n (map #(* % %) (range))).",
      },
      {
        prompt: "Write `powers-of-two` that returns the first n powers of two starting at 1, using iterate. e.g. (powers-of-two 5) => (1 2 4 8 16).",
        starter: "(defn powers-of-two [n]\n  )",
        tests: ["(= (powers-of-two 5) '(1 2 4 8 16))", "(= (powers-of-two 1) '(1))"],
        solution: "(defn powers-of-two [n]\n  (take n (iterate #(* 2 %) 1)))",
        hint: "iterate #(* 2 %) starting at 1, then take n.",
      },
    ],
  },

  {
    id: "protocols",
    title: "Protocols & Records",
    group: "Advanced Clojure",
    summary: "Define shared behaviour and lightweight data types.",
    content: [
      { t: "p", text: "A protocol is a named set of functions that different data types can implement — Clojure's way of doing polymorphism (the same function name behaving appropriately for different types)." },
      { t: "code", code: "(defprotocol Shape\n  (area [s]))" },
      { t: "p", text: "That declares a protocol Shape with one function, area. It has no implementation yet — types provide that. A record is a simple, map-like data type with named fields, created with defrecord. Records can implement protocols." },
      { t: "code", code: "(defprotocol Shape\n  (area [s]))\n\n(defrecord Rectangle [width height]\n  Shape\n  (area [s] (* width height)))\n\n(area (->Rectangle 3 4))" },
      { t: "p", text: "->Rectangle is the constructor Clojure generates for you. Inside the area method, the record's fields (width, height) are available by name. Records also behave like maps, so you can read fields with keywords:" },
      { t: "code", code: "(defrecord Point [x y])\n(:x (->Point 10 20))" },
      { t: "h", text: "reify: an object without a named type" },
      { t: "p", text: "Sometimes you want a one-off implementation of a protocol without defining a whole record. reify does exactly that." },
      { t: "code", code: "(defprotocol Named\n  (the-name [_]))\n\n(def thing\n  (reify Named\n    (the-name [_] \"Custom!\")))\n\n(the-name thing)" },
      { t: "note", text: "For most everyday data, plain maps are enough. Reach for records and protocols when you have several types that should share behaviour, or when you want a bit more performance and structure." },
    ],
    exercises: [
      {
        prompt: "Define a protocol `Shape` with function `(area [s])`, and a record `Square` with a field `side` that implements it (area = side * side). The tests will build a Square and call area.",
        starter: "(defprotocol Shape\n  (area [s]))\n\n(defrecord Square [side]\n  Shape\n  (area [s] ))",
        tests: ["(= (area (->Square 5)) 25)", "(= (area (->Square 3)) 9)"],
        solution: "(defprotocol Shape\n  (area [s]))\n\n(defrecord Square [side]\n  Shape\n  (area [s] (* side side)))",
        hint: "Inside the area method, the field `side` is in scope: (* side side).",
      },
      {
        prompt: "Define a protocol `Named` with `(label [x])`, then write `make-label` that returns a reify implementing Named whose label is the given text.",
        starter: "(defprotocol Named\n  (label [x]))\n\n(defn make-label [text]\n  )",
        tests: ["(= (label (make-label \"hi\")) \"hi\")", "(= (label (make-label \"box\")) \"box\")"],
        solution: "(defprotocol Named\n  (label [x]))\n\n(defn make-label [text]\n  (reify Named\n    (label [x] text)))",
        hint: "Return (reify Named (label [x] text)).",
      },
    ],
  },

  {
    id: "files",
    title: "Working with Files & Data",
    group: "Advanced Clojure",
    summary: "How file I/O works in Clojure — and parsing file-like data in the browser.",
    content: [
      { t: "p", text: "On a normal computer (running Clojure on the JVM or Node), reading and writing files is simple. slurp reads an entire file into a string, and spit writes a string to a file:" },
      { t: "code", code: ";; This is how it looks on the JVM / Node — it can't run in the browser sandbox:\n;;\n;;   (slurp \"notes.txt\")            ; => the file's contents as a string\n;;   (spit \"out.txt\" \"hello\")       ; => writes \"hello\" to a file\n;;\n;; So instead, run this to see the idea:\n\"slurp gives you a string; the rest is just string processing\"" },
      { t: "note", text: "Because this course runs entirely in your browser's secure sandbox, there is no file system to touch — slurp/spit aren't available here. But the interesting part of 'file work' is what you do with the contents, and that's pure Clojure you CAN run below." },
      { t: "h", text: "Splitting a file into lines" },
      { t: "p", text: "Once you have file contents as a string, clojure.string/split-lines breaks it into a sequence of lines. (Here we simulate a file using a string with \\n newline characters.)" },
      { t: "code", code: "(def file-contents \"apple\\nbanana\\ncherry\")\n(clojure.string/split-lines file-contents)" },
      { t: "h", text: "Parsing structured lines" },
      { t: "p", text: "A common task is turning a CSV-like line into fields. Combine split-lines with split on commas:" },
      { t: "code", code: "(def csv \"name,age\\nAda,36\\nAlan,41\")\n(->> (clojure.string/split-lines csv)\n     (map #(clojure.string/split % #\",\")))" },
      { t: "p", text: "That's the real skill behind reading files: get the text, then transform it with the sequence tools you already know (map, filter, reduce). The source — a file, a network response, or a string — barely matters." },
    ],
    exercises: [
      {
        prompt: "Write `count-lines` that returns how many lines are in a piece of text (lines separated by newlines).",
        starter: "(defn count-lines [text]\n  )",
        tests: ["(= (count-lines \"a\\nb\\nc\") 3)", "(= (count-lines \"only one\") 1)"],
        solution: "(defn count-lines [text]\n  (count (clojure.string/split-lines text)))",
        hint: "Use clojure.string/split-lines then count the result.",
      },
      {
        prompt: "Write `parse-row` that splits a comma-separated line into a vector of fields. e.g. (parse-row \"a,b,c\") => [\"a\" \"b\" \"c\"].",
        starter: "(defn parse-row [row]\n  )",
        tests: ["(= (parse-row \"a,b,c\") [\"a\" \"b\" \"c\"])", "(= (parse-row \"one,two\") [\"one\" \"two\"])"],
        solution: "(defn parse-row [row]\n  (clojure.string/split row #\",\"))",
        hint: "Use (clojure.string/split row #\",\").",
      },
    ],
  },
];
